import { stringify } from 'csv-stringify/browser/esm/sync';
import DOMPurify from 'dompurify';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { create } from 'zustand';

import { loadAdminNominationPeriod } from '../../queries/nominations';
import { NOMINATION_MODAL_QUERY_PARAM } from './constants';

const CSV_HEADER_KEYS = ['nomination', 'nominee', 'submission', 'message'];

export const useDownloadNominationPeriodCsv = (id: string) => {
	const { t } = useTranslation('nominations', { keyPrefix: 'csv' });

	return useCallback(async () => {
		const data = await loadAdminNominationPeriod(id);

		const rows = data.nominations.map((nomination) => {
			return [
				data.name,
				nomination.nominatedUser.displayName,
				nomination.nominator.displayName,
				DOMPurify.sanitize(nomination.reason, {
					ALLOWED_TAGS: [],
				}),
			];
		});

		const headers = CSV_HEADER_KEYS.map((key) => t(key));

		const csv = stringify(rows, {
			columns: headers,
			header: true,
		});

		const blob = new Blob([csv], { type: 'text/csv' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = data.name + '.csv';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	}, [t]);
};

export const useInitNominationModal = () => {
	const [params] = useSearchParams();
	const onOpenNominationPeriod = useNominationModal((state) => state.onOpenNominationPeriod);

	useEffect(() => {
		if (params.has(NOMINATION_MODAL_QUERY_PARAM)) {
			const nominationPeriodId = params.get(NOMINATION_MODAL_QUERY_PARAM);
			onOpenNominationPeriod(nominationPeriodId);
		}
	}, []); // We're not adding any dependencies here because we only want this to run once.
};

type UseNominationModalValue = {
	id: string | null;
	isOpen: boolean;
	onOpen: () => void;
	onOpenNominationPeriod: (id: string | null) => void;
	onClose: () => void;
};

export const useNominationModal = create<UseNominationModalValue>((set) => ({
	id: null,
	isOpen: false,
	onOpen: () => set({ id: null, isOpen: true }),
	onOpenNominationPeriod: (id) => set({ id, isOpen: true }),
	onClose: () => set({ id: null, isOpen: false }),
}));
